import { html, render } from 'lit-html';
import { isNil, set, toNumber, toString, unset } from 'lodash-es';
import '../button-icon/sds-button-icon.lit';
import '../icon/sds-icon.lit';
import '../load-more/sds-load-more.lit';
import { ROW } from './_tableTreeConsts';
import { TableTreeNode } from './table-tree-node';
import { translate } from '../utilities/translate';
export class TableSubtree {
    static walkOverChildren(rows, consumer, subTreeFilter = undefined) {
        for (const rowWithChildren of rows) {
            consumer(rowWithChildren.rootNode);
            if (isNil(subTreeFilter) ||
                subTreeFilter(rowWithChildren.rootNode)) {
                this.walkOverChildren(rowWithChildren.children, consumer, subTreeFilter);
            }
        }
    }
    static walkOverSubTrees(rows, consumer, subTreeFilter = undefined) {
        for (const rowWithChildren of rows) {
            const continue_walking = consumer(rowWithChildren);
            if ((isNil(subTreeFilter) || subTreeFilter(rowWithChildren)) &&
                (isNil(continue_walking) || continue_walking)) {
                this.walkOverSubTrees(rowWithChildren.children, consumer, subTreeFilter);
            }
        }
    }
    static fromElements(elements, loadMoreHandler, expandHandler, collapseHandler, mode) {
        const rows = [];
        let index = 0;
        while (index < elements.length) {
            const [row, nextIndex] = this.createSubtree(elements, index, loadMoreHandler, expandHandler, collapseHandler, undefined, mode);
            index = nextIndex;
            rows.push(row);
        }
        return rows;
    }
    static createSubtree(elements, index, loadMoreHandler, expandHandler, collapseHandler, parent = undefined, mode = undefined) {
        const element = elements.item(index);
        if (element instanceof HTMLTableRowElement) {
            const row = TableTreeNode.fromElement(element);
            index += 1;
            let nextElement = elements.item(index);
            const children = [];
            const subtree = new TableSubtree(row, parent, mode, children, loadMoreHandler, expandHandler, collapseHandler);
            while (!isNil(nextElement) &&
                toNumber(nextElement.dataset[ROW.LEVEL]) === row.level + 1) {
                // correct expanded collapsed attributes to not force users
                // to set them manually
                if (!subtree.rootNode.isExpanded) {
                    set(nextElement.dataset, ROW.HIDDEN, '');
                }
                const [child, nextIndex] = this.createSubtree(elements, index, loadMoreHandler, expandHandler, collapseHandler, subtree, mode);
                children.push(child);
                nextElement = elements.item(nextIndex);
                index = nextIndex;
            }
            subtree.children = children;
            return [subtree, index];
        }
        else {
            throw Error('table child was not of HTMLTableRowElement type');
        }
    }
    constructor(rootNode, parentTree, mode, children = [], loadMoreHandler, expandHandler, collapseHandler) {
        this.rootNode = rootNode;
        this.parentTree = parentTree;
        this.mode = mode;
        this.children = children;
        this.loadMoreHandler = loadMoreHandler;
        this.expandHandler = expandHandler;
        this.collapseHandler = collapseHandler;
        this.loadMoreRow = undefined;
        this.noDataRow = undefined;
        const parentElement = rootNode.innerElement.parentElement;
        if (!isNil(parentElement)) {
            const loadMore = !isNil(rootNode.identifier)
                ? parentElement.querySelector(`tr[data-id="${rootNode.identifier}"][data-load-more][data-level="${rootNode.level + 1}"]`)
                : null;
            if (!isNil(loadMore)) {
                loadMore.remove();
            }
        }
        else {
            console.error('element %o was detached from node but was registered as tree node', rootNode.innerElement);
        }
    }
    get needsLoadMoreButton() {
        return !this.areChildrenLoaded() && this.rootNode.isExpanded;
    }
    get needsNoDataRow() {
        return this.areChildrenLoaded() && this.rootNode.isExpanded;
    }
    get hasChildren() {
        return (this.children.length > 0 ||
            this.rootNode.childrenCount > 0 ||
            !this.rootNode.areChildrenLoaded);
    }
    get lastDescendant() {
        if (this.children.length === 0) {
            return this.rootNode;
        }
        else {
            return this.children[this.children.length - 1].lastDescendant;
        }
    }
    areChildrenLoaded() {
        return this.rootNode.childrenCount <= this.children.length;
    }
    addLoadMoreButtonsToSubtree() {
        if (this.mode !== 'list' && !this.rootNode.isHidden) {
            this.addLoadMoreButtonToCurrentRoot();
            this.children.forEach(child => child.addLoadMoreButtonsToSubtree());
        }
    }
    addNoDataToSubtree() {
        if (this.mode !== 'list' &&
            !this.hasChildren &&
            this.rootNode.childrenCount === 0) {
            this.addNoDataToCurrentRoot();
            if (!this.rootNode.isExpanded || this.rootNode.isHidden) {
                this.hideNoData();
            }
        }
        else if (this.hasChildren) {
            this.children.forEach(child => child.addNoDataToSubtree());
        }
    }
    addSubtreePathsAndControls() {
        this.addTreePathAndControl();
        this.children.forEach(child => child.addSubtreePathsAndControls());
        this.drawSubtreePaths();
    }
    drawSubtreePaths() {
        if (this.parentTree === undefined) {
            this.drawAllSubtreePathsFromCurrentRoot();
        }
        else {
            this.parentTree.drawSubtreePaths();
        }
    }
    addTreePathAndControl() {
        if (this.hasChildren) {
            this.rootNode.addParentControls(() => this.toggle(), this.mode === 'list' && !this.areSiblingsLoaded());
            this.rootNode.addTreePath(this.hasChildren);
        }
        else if (this.noDataRow !== undefined) {
            this.rootNode.addStyleClass(ROW.PARENT);
            this.rootNode.addParentControls(() => this.toggle(), this.mode === 'list' && !this.areSiblingsLoaded());
            this.rootNode.addTreePath(true);
        }
        else if (this.rootNode.isChild) {
            this.rootNode.collapse();
            this.rootNode.addChildControls();
        }
    }
    /**
     *  Find out if all descendants are loaded.
     *  <br>
     *  *Note: Not be confused with all children.*
     */
    areSiblingsLoaded() {
        return (this.areChildrenLoaded() &&
            !this.children.some(child => !child.areSiblingsLoaded()));
    }
    toggle() {
        if (this.rootNode.isExpanded) {
            this.collapse();
        }
        else {
            this.expand();
        }
        this.drawSubtreePaths();
    }
    expand() {
        this.expandRoot();
        this.revealChildren();
        this.drawSubtreePaths();
    }
    collapse() {
        this.collapseRoot();
        this.collapseChildren();
        this.drawSubtreePaths();
    }
    revealLoadMore() {
        if (this.loadMoreRow !== undefined) {
            unset(this.loadMoreRow.dataset, ROW.HIDDEN);
        }
        else {
            this.addLoadMoreButtonToCurrentRoot();
        }
    }
    revealNoData() {
        if (this.noDataRow !== undefined) {
            unset(this.noDataRow.dataset, ROW.HIDDEN);
        }
        else if (!this.hasChildren) {
            this.addNoDataToCurrentRoot();
        }
    }
    expandRoot() {
        if (this.hasChildren) {
            this.rootNode.expand();
            this.expandHandler(this.rootNode.identifier, this.rootNode.level);
            this.revealLoadMore();
        }
        else if (!this.hasChildren && this.rootNode.childrenCount === 0) {
            this.expandNoData();
        }
    }
    revealChildren() {
        if (this.rootNode.isExpanded) {
            this.revealLoadMore();
            this.revealNoData();
            this.children.forEach(child => {
                child.rootNode.reveal();
                child.revealChildren();
            });
        }
    }
    expandNoData() {
        if (this.noDataRow) {
            set(this.rootNode.innerElement.dataset, ROW.EXPANDED, '');
        }
    }
    collapseRoot() {
        if (this.hasChildren) {
            this.rootNode.collapse();
            this.hideLoadMore();
            this.collapseHandler(this.rootNode.identifier, this.rootNode.level);
        }
        else if (!this.hasChildren && this.rootNode.childrenCount === 0) {
            this.collapseNoData();
        }
    }
    collapseChildren() {
        this.hideLoadMore();
        this.hideNoData();
        this.children.forEach(child => {
            child.rootNode.hide();
            child.collapseChildren();
        });
    }
    collapseNoData() {
        if (this.noDataRow) {
            unset(this.rootNode.innerElement.dataset, ROW.EXPANDED);
        }
    }
    hideLoadMore() {
        if (this.loadMoreRow !== undefined) {
            set(this.loadMoreRow.dataset, ROW.HIDDEN, '');
        }
    }
    hideNoData() {
        if (this.noDataRow !== undefined) {
            set(this.noDataRow.dataset, ROW.HIDDEN, '');
        }
    }
    drawAllSubtreePathsFromCurrentRoot() {
        let height = this.rootNode.innerElementHeight - 20;
        TableSubtree.walkOverSubTrees(this.children.slice(0, this.children.length - 1), child => {
            height += child.rootNode.innerElementHeight;
            if (child.noDataRow) {
                height += 40;
            }
            if (child.needsLoadMoreButton && child.rootNode.isExpanded) {
                height += 40;
            }
        }, child => child.rootNode.isExpanded);
        if (this.children.length > 0) {
            height += 20;
        }
        if (this.noDataRow !== undefined) {
            height += 21;
        }
        this.rootNode.setTreePathHeight(height);
        this.children.forEach(child => child.drawAllSubtreePathsFromCurrentRoot());
    }
    addLoadMoreButtonToCurrentRoot() {
        var _a;
        if (this.loadMoreRow === undefined && this.needsLoadMoreButton) {
            const table = (_a = this.rootNode.innerElement
                .closest('sds-table-tree')) === null || _a === void 0 ? void 0 : _a.querySelector('table');
            const identifier = this.rootNode.identifier;
            if (!isNil(table) && !isNil(identifier)) {
                const loadMoreRow = table.insertRow(this.lastDescendant.innerElement.rowIndex + 1);
                set(loadMoreRow.dataset, ROW.LOAD_MORE, '');
                set(loadMoreRow.dataset, ROW.LEVEL, toString(this.rootNode.level + 1));
                set(loadMoreRow.dataset, ROW.IDENTIFIER, identifier);
                loadMoreRow.classList.add(ROW.CHILD);
                const loadMoreCell = loadMoreRow.insertCell(0);
                render(html `
                        <sds-button
                            label="Load More"
                            size="md"
                            type="link"
                            icon="refresh"
                            @click="${() => this.loadMoreHandler(identifier, this.rootNode.level, this.children.length)}"
                        ></sds-button>
                    `, loadMoreCell);
                const loadMoreInfoCell = loadMoreRow.insertCell(1);
                loadMoreInfoCell.colSpan =
                    this.rootNode.innerElement.cells.length - 1;
                render(html ` <span>${this.children.length}</span>
                        <bdi>${translate('sds.from')}</bdi> ${this.rootNode
                    .childrenCount}`, loadMoreInfoCell);
                render(html `
                        <div class="sds-loaded-info">
                            <span>${this.children.length}</span>
                            <bdi>${translate('sds.from')}</bdi>
                            ${this.rootNode.childrenCount}
                        </div>
                    `, loadMoreInfoCell);
                this.loadMoreRow = loadMoreRow;
            }
            else if (isNil(table)) {
                console.error('could not find nested table in sds-table-tree');
            }
            else if (isNil(identifier)) {
                console.error('could not add load more row on parent without identifier %o', this.rootNode.innerElement);
            }
        }
    }
    addNoDataToCurrentRoot() {
        var _a;
        if (this.noDataRow === undefined && this.needsNoDataRow) {
            const table = (_a = this.rootNode.innerElement
                .closest('sds-table-tree')) === null || _a === void 0 ? void 0 : _a.querySelector('table');
            const identifier = this.rootNode.identifier;
            if (!isNil(table) && !isNil(identifier)) {
                const noDataRow = table.insertRow(this.lastDescendant.innerElement.rowIndex + 1);
                set(noDataRow.dataset, ROW.NO_RESULTS, '');
                set(noDataRow.dataset, ROW.LEVEL, toString(this.rootNode.level + 1));
                set(noDataRow.dataset, ROW.IDENTIFIER, identifier);
                noDataRow.classList.add(ROW.CHILD);
                const noDataCell = noDataRow.insertCell(0);
                render(html `
                        <div style="height: 40px">
                            <sds-td value="No Data"></sds-td>
                        </div>
                    `, noDataCell);
                this.generateTds(noDataRow, this.rootNode.innerElement.cells.length);
                this.noDataRow = noDataRow;
                TableTreeNode.fromElement(noDataRow).addChildControls();
            }
            else if (isNil(table)) {
                console.error('could not find nested table in sds-table-tree');
            }
            else if (isNil(identifier)) {
                console.error('could not add no data row on parent without identifier %o', this.rootNode.innerElement);
            }
        }
    }
    generateTds(row, columnNumber) {
        for (let i = 1; i < columnNumber; ++i) {
            render(html ` <td></td> `, row.insertCell(i));
        }
    }
}
